// services/organization.service.js
import httpClient from './httpClient';
import TokenService from './token.service';
import { extractDetailMessage, extractDetailError } from '../utils/errorDetail';

/**
 * Organization Service
 * Maneja todas las operaciones relacionadas con Organizations
 * Base URL configured via REACT_APP_AUTH_API_URL or REACT_APP_API_BASE_URL
 */

// Usar cliente HTTP centralizado
const authClient = httpClient;

/**
 * Normalizes error payloads from modern HTTP-status-based responses.
 * The new backend returns `error` as a string; legacy endpoints may return an array.
 * Returns null when the body carries no error message (e.g. 409 body is the conflicting resource).
 * UI handlers should fall back to the HTTP status label in that case.
 * @param {Error} error - Axios error object
 * @returns {string[] | null}
 */
const normalizeOrgError = (error) => {
  const errorData = error.response?.data;
  const raw = errorData?.error;
  if (Array.isArray(raw) && raw.length > 0) return raw;
  if (raw && typeof raw === 'string') return [raw];
  if (errorData?.message) return [errorData.message];
  if (errorData?.detail) return [extractDetailMessage(errorData.detail)];
  return null; // body had no error text (e.g. 409 returns the conflicting resource)
};

/**
 * Sobre de fallo común a todos los métodos. Sobre el mensaje aplanado agrega
 * `code` y `context`, que es lo que `resolveAccountDomainError` necesita para
 * elegir el copy sin leer el texto del mensaje.
 * @param {Error} error - Axios error object
 * @returns {{success: false, status: number|undefined, code: string|null, context: object|null, error: string[]|null}}
 */
const orgErrorResult = (error) => {
  const { code, context } = extractDetailError(error.response?.data?.detail);
  return {
    success: false,
    status: error.response?.status,
    code,
    context,
    error: normalizeOrgError(error),
  };
};

const OrganizationService = {
  /**
   * Crear nueva organización
   * POST /organizations
   */
  async createOrganization(organizationData) {
    try {
      const response = await authClient.post('/organizations', organizationData);
      return {
        success: true,
        status: response.status,
        data: response.data.body
      };
    } catch (error) {
      console.error('❌ Error creating organization:', error);
      console.error('Error response:', error.response?.data);
      return orgErrorResult(error);
    }
  },

  /**
   * Obtener detalles de una organización
   * GET /organizations/{organization_id}
   * Este endpoint obtiene la información completa de una organización para cuando se selecciona en el switcher
   */
  async getOrganization(organizationId) {
    try {
      const response = await authClient.get(`/organizations/${organizationId}`);
      return {
        success: true,
        status: response.status,
        data: response.data.body
      };
    } catch (error) {
      console.error('❌ Error fetching organization:', error);
      return orgErrorResult(error);
    }
  },

  /**
   * Actualizar organización
   * PUT /organizations/{organization_id}
   */
  async updateOrganization(organizationId, updates) {
    try {
      const response = await authClient.put(`/organizations/${organizationId}`, updates);
      return {
        success: true,
        status: response.status,
        data: response.data.body
      };
    } catch (error) {
      console.error('❌ Error updating organization:', error);
      console.error('Error response:', error.response?.data);
      return orgErrorResult(error);
    }
  },

  /**
   * Eliminar organización
   * DELETE /organizations/{organization_id}
   */
  async deleteOrganization(organizationId) {
    try {
      const response = await authClient.delete(`/organizations/${organizationId}`);
      return {
        success: true,
        status: response.status,
        data: response.data?.body ?? response.data
      };
    } catch (error) {
      console.error('Error deleting organization:', error);
      return orgErrorResult(error);
    }
  },

  /**
   * Cerrar/Desactivar organización
   * POST /organizations/{organization_id}/close
   * @param {string} organizationId - UUID de la organización
   * @param {string} [reason] - Razón opcional para cerrar la organización
   */
  async closeOrganization(organizationId, reason = null) {
    try {
      const body = reason ? { reason } : {};
      const response = await authClient.post(`/organizations/${organizationId}/close`, body);
      return {
        success: true,
        status: response.status,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error closing organization:', error);
      return orgErrorResult(error);
    }
  },

  /**
   * Listar organizaciones del usuario
   * GET /organizations
   */
  async listOrganizations() {
    try {
      const response = await authClient.get('/organizations');
      // Backend returns { status_code, message, error, body: { organizations: [], total_count: N } }
      const raw = response.data.body ?? response.data;
      const orgs =
        Array.isArray(raw?.organizations) ? raw.organizations :
        Array.isArray(raw) ? raw :
        [];
      const totalCount = raw?.total_count ?? orgs.length;
      return {
        success: true,
        status: response.status,
        data: orgs,
        totalCount
      };
    } catch (error) {
      // Silenciar 404 ya que el endpoint puede no estar implementado aún
      if (error.response?.status === 404) {
        console.warn('Organizations endpoint (GET /organizations) not implemented yet');
      } else {
        console.error('Error listing organizations:', error);
      }
      return { ...orgErrorResult(error), data: [], totalCount: 0 };
    }
  },

  /**
   * Agregar miembro a organización
   * POST /organizations/{organization_id}/members
   * @param {string} organizationId - UUID de la organización
   * @param {Object} memberData - Datos del miembro
   * @param {string} memberData.email - Email del usuario (requerido)
   * @param {string} [memberData.role='MEMBER'] - Rol del miembro (MEMBER, ADMIN, OWNER, VIEWER)
   * @param {string} [memberData.request_id] - ID de solicitud opcional
   */
  async addMember(organizationId, memberData) {
    try {
      const payload = {
        email: memberData.email,
        role: memberData.role || 'MEMBER'
      };
      if (memberData.request_id) {
        payload.request_id = memberData.request_id;
      }

      const response = await authClient.post(
        `/organizations/${organizationId}/members`,
        payload
      );

      return {
        success: true,
        status: response.status,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error adding member:', error);
      return orgErrorResult(error);
    }
  },

  /**
   * Remover miembro de organización
   * DELETE /organizations/{organization_id}/members/{user_id}
   */
  async removeMember(organizationId, userId) {
    try {
      const response = await authClient.delete(
        `/organizations/${organizationId}/members/${userId}`
      );
      return {
        success: true,
        status: response.status,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error removing member:', error);
      return orgErrorResult(error);
    }
  },

  /**
   * Actualizar rol de miembro
   * PUT /organizations/{organization_id}/members/{user_id}
   */
  async updateMemberRole(organizationId, userId, newRole, requestId = null) {
    try {
      const payload = { role: newRole };
      if (requestId) payload.request_id = requestId;
      
      const response = await authClient.put(
        `/organizations/${organizationId}/members/${userId}/role`,
        payload
      );
      return {
        success: true,
        status: response.status,
        data: response.data.body
      };
    } catch (error) {
      console.error('Error updating member role:', error);
      return orgErrorResult(error);
    }
  },

  /**
   * Listar miembros de organización
   * GET /organizations/{organization_id}/members
   * @param {string} organizationId - UUID de la organización
   * @param {Object} options - Opciones de filtrado
   * @param {boolean} [options.includeInactive=false] - Incluir miembros inactivos
   * @param {string} [options.roleFilter] - Filtrar por rol (MEMBER, ADMIN, OWNER)
   */
  async listMembers(organizationId, options = {}) {
    try {
      const params = {};
      if (options.includeInactive !== undefined) {
        params.include_inactive = options.includeInactive;
      }
      if (options.roleFilter) {
        params.role_filter = options.roleFilter;
      }

      const response = await authClient.get(
        `/organizations/${organizationId}/members`,
        { params }
      );
      const raw = response.data.body ?? response.data;
      return {
        success: true,
        status: response.status,
        data: Array.isArray(raw?.members) ? raw.members : (Array.isArray(raw) ? raw : []),
        totalCount: raw?.total_count ?? 0
      };
    } catch (error) {
      console.error('Error listing members:', error);
      return { ...orgErrorResult(error), data: [], totalCount: 0 };
    }
  },

  /**
   * Buscar organizaciones
   * GET /organizations/search?term=...&active_only=...&limit=...&offset=...
   */
  async searchOrganizations({ 
    term, 
    activeOnly = true, 
    limit = 10, 
    offset = 0 
  } = {}) {
    try {
      const response = await authClient.get('/organizations/search', {
        params: {
          term,
          active_only: activeOnly,
          limit,
          offset
        }
      });
      const raw = response.data.body ?? response.data;
      return {
        success: true,
        status: response.status,
        data: Array.isArray(raw?.organizations) ? raw.organizations : (Array.isArray(raw) ? raw : []),
        totalCount: raw?.total_count ?? 0,
        searchTerm: raw?.search_term
      };
    } catch (error) {
      console.error('Error searching organizations:', error);
      return { ...orgErrorResult(error), data: [], totalCount: 0 };
    }
  }
};

export default OrganizationService;
